import { useState, useEffect } from 'react';
import { Search, X, Check, Users, UserPlus, UserMinus, AlertCircle } from 'lucide-react';
import api from '../services/api';

const UserSelector = ({
    selectedUsers = [],
    onUsersChange,
    placeholder = "Select users...",
    maxUsers = 10,
    showSearch = true
}) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (showDropdown) {
            fetchUsers();
        }
    }, [showDropdown, searchTerm]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const params = new URLSearchParams({
                search: searchTerm,
                limit: 50
            });

            const response = await api.get(`/cameras/users?${params}`);
            setUsers(response.data.data.users);
        } catch (error) {
            console.error('Error fetching users:', error);
            setError('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const handleUserToggle = (user) => {
        const isSelected = selectedUsers.some(u => u._id === user._id);

        if (isSelected) {
            // Remove user
            const newUsers = selectedUsers.filter(u => u._id !== user._id);
            onUsersChange(newUsers);
        } else {
            // Add user (check max limit)
            if (selectedUsers.length >= maxUsers) {
                alert(`Maximum ${maxUsers} users can be selected`);
                return;
            }
            const newUsers = [...selectedUsers, user];
            onUsersChange(newUsers);
        }
    };

    const removeUser = (userId) => {
        const newUsers = selectedUsers.filter(u => u._id !== userId);
        onUsersChange(newUsers);
    };

    const filteredUsers = users.filter(user =>
        !selectedUsers.some(selected => selected._id === user._id)
    );

    return (
        <div className="space-y-4">
            {/* Selected Users Display */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                        Assigned Users
                    </label>
                    <span className="text-xs text-gray-500">
                        {selectedUsers.length}/{maxUsers}
                    </span>
                </div>

                {selectedUsers.length === 0 ? (
                    <div className="text-sm text-gray-500 italic bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                        No users assigned to this camera
                    </div>
                ) : (
                    <div className="space-y-2">
                        {selectedUsers.map(user => (
                            <div
                                key={user._id}
                                className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3"
                            >
                                <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                        <span className="text-blue-600 font-medium text-sm">
                                            {user.username.charAt(0).toUpperCase()}
                                        </span>
                                    </div>
                                    <div>
                                        <div className="font-medium text-blue-900 text-sm">
                                            {user.username}
                                        </div>
                                        <div className="text-blue-600 text-xs">
                                            {user.email}
                                        </div>
                                        {user.role && (
                                            <div className="text-blue-500 text-xs">
                                                {user.role.name}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={() => removeUser(user._id)}
                                    className="text-blue-600 hover:text-blue-800 p-1 rounded transition-colors"
                                    title="Remove user"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* User Selection Dropdown */}
            <div className="relative">
                <button
                    type="button"
                    onClick={() => setShowDropdown(!showDropdown)}
                    disabled={selectedUsers.length >= maxUsers}
                    className={`w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-lg bg-white text-left text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${selectedUsers.length >= maxUsers
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'hover:border-gray-400'
                        }`}
                >
                    <span className="flex items-center">
                        <Users className="h-4 w-4 mr-2 text-gray-400" />
                        {selectedUsers.length >= maxUsers
                            ? `Maximum ${maxUsers} users reached`
                            : placeholder
                        }
                    </span>
                    <UserPlus className="h-4 w-4 text-gray-400" />
                </button>

                {showDropdown && selectedUsers.length < maxUsers && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {/* Search */}
                        {showSearch && (
                            <div className="p-3 border-b border-gray-200 bg-gray-50">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search users by name, email, or username..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Users List */}
                        <div className="py-1">
                            {loading ? (
                                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                                    Loading users...
                                </div>
                            ) : error ? (
                                <div className="px-4 py-3 text-sm text-red-500 text-center">
                                    <AlertCircle className="h-4 w-4 mx-auto mb-2" />
                                    {error}
                                </div>
                            ) : filteredUsers.length === 0 ? (
                                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                    {searchTerm ? 'No users found matching your search' : 'No users available'}
                                </div>
                            ) : (
                                filteredUsers.map(user => (
                                    <button
                                        key={user._id}
                                        onClick={() => handleUserToggle(user)}
                                        className="w-full flex items-center justify-between px-4 py-3 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-colors"
                                    >
                                        <div className="flex items-center space-x-3">
                                            <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                                                <span className="text-indigo-600 font-medium text-sm">
                                                    {user.username.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                            <div>
                                                <div className="font-medium text-gray-900">
                                                    {user.username}
                                                </div>
                                                <div className="text-gray-500 text-xs">
                                                    {user.email}
                                                </div>
                                                {user.role && (
                                                    <div className="text-gray-400 text-xs">
                                                        {user.role.name}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <UserPlus className="h-4 w-4 text-gray-400" />
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Help Text */}
            <div className="text-xs text-gray-500">
                <p>• Users can access cameras through roles or direct assignment</p>
                <p>• Maximum {maxUsers} users can be assigned per camera</p>
                <p>• Only active users are shown in the list</p>
            </div>
        </div>
    );
};

export default UserSelector; 